import { Game } from '../../../../../features/game/types/Game'
import { Setting } from '../../../../../types/Setting'
import { ComparedGame, FeaturedGamesSetting, FeaturedGamesSettingData } from '../../types/types'

export const maxComparedGames = 4

export type FeaturedGamesField = 'featuredGames' | 'compareOrder' | 'value'

// Legacy's own Save writes `''`, which its parser then crashed on.
export const parseCompareOrder = (compareOrder: string | undefined | null): { gameId: string; marketIso: string }[] => {
  if (!compareOrder) return []
  const [idsPart = '', isosPart = ''] = compareOrder.split('&')
  const isos = isosPart.split(',')
  return idsPart
    .split(',')
    .map((gameId, index) => ({ gameId: gameId.trim(), marketIso: (isos[index] ?? '').trim() }))
    .filter(({ gameId }) => gameId)
}

export const findGameMarketIso = (game: Pick<Game, 'markets'> | undefined, marketIso: string): string | undefined => {
  const markets = game?.markets ?? []
  const market =
    markets.find((candidate) => candidate.iso === marketIso) ?? markets.find((candidate) => candidate.iso.toLowerCase() === marketIso.toLowerCase())
  return (market ?? markets[0])?.iso
}

// Keeps featured games that have a market, at most `maxComparedGames`, each once.
export const resolveComparedGames = (
  parsed: { gameId: string; marketIso: string }[],
  featuredGameIds: string[],
  gamesById: Record<string, Pick<Game, 'markets'> | undefined>
): ComparedGame[] =>
  parsed.reduce<ComparedGame[]>((compared, { gameId, marketIso }) => {
    if (compared.length >= maxComparedGames || !featuredGameIds.includes(gameId) || compared.some((candidate) => candidate.gameId === gameId)) return compared
    const resolvedIso = findGameMarketIso(gamesById[gameId], marketIso)
    return resolvedIso ? [...compared, { gameId, marketIso: resolvedIso }] : compared
  }, [])

export const buildCompareOrder = (compared: ComparedGame[]): string =>
  compared.length ? `${compared.map(({ gameId }) => gameId).join(',')}&${compared.map(({ marketIso }) => marketIso).join(',')}` : ''

// Legacy's body: the stored setting with the games of `data` replaced and `value` set to the ids.
export const buildFeaturedGamesSetting = (
  stored: Setting<FeaturedGamesSettingData> | null | undefined,
  featuredGameIds: string[],
  compared: ComparedGame[]
): FeaturedGamesSetting => ({
  ...stored,
  name: 'featured_games',
  value: featuredGameIds.join(','),
  data: { ...stored?.data, featuredGames: featuredGameIds, compareOrder: buildCompareOrder(compared) },
  publicSetting: true,
})

export const getUnstoredFeaturedGamesFields = (
  sent: Pick<FeaturedGamesSetting, 'data' | 'value'>,
  stored: Pick<FeaturedGamesSetting, 'data' | 'value'> | null | undefined
): FeaturedGamesField[] => {
  const unstored: FeaturedGamesField[] = []
  const sentIds = sent.data.featuredGames ?? []
  const storedIds = stored?.data?.featuredGames ?? []
  if (sentIds.length !== storedIds.length || sentIds.some((id, index) => id !== storedIds[index])) unstored.push('featuredGames')
  if ((sent.data.compareOrder ?? '') !== (stored?.data?.compareOrder ?? '')) unstored.push('compareOrder')
  if ((sent.value ?? '') !== (stored?.value ?? '')) unstored.push('value')
  return unstored
}

export const featuredGamesFieldLabelKeys: Record<FeaturedGamesField, string> = {
  featuredGames: 'internal-service-management:settings_featured_field_games',
  compareOrder: 'internal-service-management:settings_featured_field_compare_order',
  value: 'internal-service-management:settings_featured_field_value',
}
